import type { AppTableSelectOption } from '@shared/ui/selects'
import type { AppPaginationItem, AppPaginationProps } from '../types/interfaces'

interface AppPaginationOptions {
  props: AppPaginationProps
}

interface AppPaginationReturn {
  items: ComputedRef<AppPaginationItem[]>
  perPageSelectOptions: ComputedRef<AppTableSelectOption[]>
  title: ComputedRef<string>
}

export const ROWS_PER_PAGE_OPTIONS = [15, 25, 50, 100, 150]
const VISIBLE_WITHOUT_ELLIPSIS = 7

const buildPageItems = (current: number, last: number, disabled: boolean): AppPaginationItem[] => {
  const items: AppPaginationItem[] = []

  items.push({
    variant: 'prev',
    label: 'prev',
    page: current > 1 ? current - 1 : null,
    active: false,
    disabled: disabled || current <= 1,
  })

  const pages: (number | 'ellipsis')[] = []
  if (last <= VISIBLE_WITHOUT_ELLIPSIS) {
    for (let i = 1; i <= last; i++) pages.push(i)
  } else {
    pages.push(1)
    const left = Math.max(2, current - 1)
    const right = Math.min(last - 1, current + 1)
    if (left > 2) pages.push('ellipsis')
    for (let i = left; i <= right; i++) pages.push(i)
    if (right < last - 1) pages.push('ellipsis')
    pages.push(last)
  }

  pages.forEach((entry) => {
    if (entry === 'ellipsis') {
      items.push({
        variant: 'ellipsis',
        label: '…',
        page: null,
        active: false,
        disabled: true,
      })
      return
    }
    items.push({
      variant: 'page',
      label: String(entry),
      page: entry,
      active: entry === current,
      disabled,
    })
  })

  items.push({
    variant: 'next',
    label: 'next',
    page: current < last ? current + 1 : null,
    active: false,
    disabled: disabled || current >= last,
  })

  return items
}

export const useAppPagination = ({ props }: AppPaginationOptions): AppPaginationReturn => {
  const items = computed<AppPaginationItem[]>(() => buildPageItems(
    props.meta.current_page ?? 1,
    props.meta.last_page ?? 1,
    props.disabled ?? false,
  ))

  const perPageSelectOptions = computed<AppTableSelectOption[]>(() => {
    const options = props.perPageOptions ?? ROWS_PER_PAGE_OPTIONS
    return options.map(value => ({
      value,
      text: String(value),
    }))
  })

  const title = computed<string>(() => {
    const total = props.meta.total ?? 0
    const from = props.meta.from ?? 0
    const to = props.meta.to ?? 0
    if (!total) return 'Showing 0 results'
    return `Showing ${from} to ${to} of ${total} results`
  })

  return {
    items,
    perPageSelectOptions,
    title,
  }
}
